import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Play, Pause, AlertTriangle, Wifi, WifiOff, Smartphone, LogOut, Loader, Camera, Video, X, Battery, BatteryCharging, Clock, ExternalLink } from 'lucide-react';
import { doc, updateDoc, setDoc, onSnapshot, collection, addDoc, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';

// Genera un SVG inline del retrato mostrando solo las posiciones ya hiladas.
const buildProgressSvg = (positions, ejecutadasHasta, totalClavos, size) => {
  if (!positions || positions.length === 0) return null;
  const radius = (size / 2) - 4;
  const center = size / 2;
  const tc = totalClavos || 250;

  const getPoint = (idx) => {
    const angle = (idx * 360 / tc);
    const rad = (angle * Math.PI) / 180;
    return { x: center + radius * Math.cos(rad), y: center + radius * Math.sin(rad) };
  };

  // Marcas de referencia (0, 1/4, 1/2, 3/4)
  const refPoints = [0, Math.round(tc / 4), Math.round(tc / 2), Math.round(tc * 3 / 4)]
    .map(idx => {
      const p = getPoint(idx);
      return `<circle cx="${p.x}" cy="${p.y}" r="2" fill="#475569"/>`;
    })
    .join('');

  // Sólo las líneas ya hiladas (de 0 a ejecutadasHasta)
  const limite = Math.min(ejecutadasHasta + 1, positions.length);
  const lines = [];
  for (let i = 1; i < limite; i++) {
    const a = getPoint(positions[i - 1]);
    const b = getPoint(positions[i]);
    lines.push(`<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="white" stroke-width="0.4" stroke-opacity="0.85"/>`);
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="100%" height="100%">
    <circle cx="${center}" cy="${center}" r="${radius}" fill="#0f172a" stroke="#1e293b" stroke-width="1"/>
    ${refPoints}
    ${lines.join('')}
  </svg>`;
};

const formatTiempo = (segs) => {
  if (segs == null || isNaN(segs)) return '—';
  if (segs < 60) return `${segs}s`;
  const m = Math.floor(segs / 60);
  const s = segs % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
};

const RemoteControl = ({ db, user, estado, sessionId, isAdmin, onSignOut, onUsarAppCompleta }) => {
  // Comando pendiente: { accion, id, sentAt }
  const [comandoPendiente, setComandoPendiente] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const timeoutAckRef = useRef(null);

  // Detecta ack del último comando enviado
  useEffect(() => {
    if (!comandoPendiente) return;
    if (estado?.ultimoComandoProcesadoId === comandoPendiente.id) {
      setComandoPendiente(null);
      setEnviando(false);
      setError(null);
      if (timeoutAckRef.current) {
        clearTimeout(timeoutAckRef.current);
        timeoutAckRef.current = null;
      }
    }
  }, [estado?.ultimoComandoProcesadoId, comandoPendiente]);

  // Timeout de ack: 6s
  useEffect(() => {
    if (!comandoPendiente) return;
    timeoutAckRef.current = setTimeout(() => {
      setError('La tablet no respondió. Reintenta.');
      setEnviando(false);
      setComandoPendiente(null);
    }, 6000);
    return () => {
      if (timeoutAckRef.current) {
        clearTimeout(timeoutAckRef.current);
        timeoutAckRef.current = null;
      }
    };
  }, [comandoPendiente]);

  const enviarComando = async (accion) => {
    if (enviando) return;
    setError(null);
    setEnviando(true);
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      const ref = doc(db, 'controlRemoto', user.uid);
      await updateDoc(ref, {
        comando: { accion, id, timestamp: serverTimestamp() }
      });
      setComandoPendiente({ accion, id });
    } catch (err) {
      console.error('Error enviando comando:', err);
      setError('Error enviando comando: ' + err.message);
      setEnviando(false);
    }
  };

  // ── Estados derivados ──────────────────────────────────────────────────
  const ejecutando = !!estado?.ejecutando;
  const pausado = !!estado?.pausado;
  const esperandoTimelapse = !!estado?.esperandoTimelapse;
  const bleConectado = !!estado?.bleConectado;
  const totalPos = estado?.totalPosiciones || 0;
  const idxActual = estado?.posicionActualIndex ?? 0;
  const progresoPct = totalPos > 0 ? Math.round((idxActual / totalPos) * 100) : 0;
  const tiempoRestante = estado?.tiempoRestante;
  const alertas = estado?.alertas || {};
  const modo = estado?.modo || 'hilado';
  const bateria = estado?.bateria; // { level, charging } | null

  // Hora estimada de fin (absoluta): solo válida cuando está ejecutando y no pausado
  const horaEstimada = useMemo(() => {
    if (!ejecutando || pausado) return null;
    if (tiempoRestante == null || isNaN(tiempoRestante) || tiempoRestante <= 0) return null;
    const fin = new Date(Date.now() + tiempoRestante * 1000);
    const hh = fin.getHours().toString().padStart(2, '0');
    const mm = fin.getMinutes().toString().padStart(2, '0');
    // Si pasa de la medianoche, mostrar día
    const ahora = new Date();
    const dif = (fin - ahora) / (1000 * 60 * 60); // horas
    const cruzaDia = fin.getDate() !== ahora.getDate();
    const etiqueta = cruzaDia ? (dif > 24 ? ' (+2d)' : ' (mañana)') : '';
    return `${hh}:${mm}${etiqueta}`;
  }, [ejecutando, pausado, tiempoRestante]);

  // SVG de progreso
  const svgMarkup = useMemo(() => {
    if (modo === 'clavado' || !estado?.posiciones?.length) return null;
    return buildProgressSvg(estado.posiciones, idxActual, estado.totalClavos || 250, 200);
  }, [estado?.posiciones, idxActual, estado?.totalClavos, modo]);

  // ── VIDEO STREAMING (RECEPTOR) ────────────────────────────────────────
  const [videoModal, setVideoModal] = useState(false);
  const [videoConectando, setVideoConectando] = useState(false);
  const [videoConectado, setVideoConectado] = useState(false);
  const [videoErr, setVideoErr] = useState(null);
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const videoSessionRef = useRef(null);
  const videoUnsubsRef = useRef([]);

  const stopVideo = useCallback(() => {
    videoUnsubsRef.current.forEach(u => { try { u(); } catch (e) {} });
    videoUnsubsRef.current = [];
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (e) {}
      pcRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    videoSessionRef.current = null;
    setVideoConectando(false);
    setVideoConectado(false);
    setVideoModal(false);
    if (user) {
      updateDoc(doc(db, 'controlRemoto', user.uid), {
        'video.solicitado': false,
        'video.sessionId': null,
        'video.activo': false,
        'video.offer': null,
        'video.answer': null
      }).catch(() => {});
    }
  }, [user, db]);

  const startVideo = useCallback(async () => {
    if (videoConectando || videoConectado) return;
    setVideoErr(null);
    setVideoConectando(true);
    setVideoModal(true);

    const sId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    videoSessionRef.current = sId;

    try {
      // Limpiar ICE anteriores
      const iceTabletRef = collection(db, 'controlRemoto', user.uid, 'videoIceTablet');
      const icePhoneRef = collection(db, 'controlRemoto', user.uid, 'videoIcePhone');
      const [oldT, oldP] = await Promise.all([getDocs(iceTabletRef), getDocs(icePhoneRef)]);
      await Promise.all([...oldT.docs, ...oldP.docs].map(d => deleteDoc(d.ref)));

      // Solicitar video
      await setDoc(doc(db, 'controlRemoto', user.uid), {
        video: { solicitado: true, sessionId: sId, activo: false, offer: null, answer: null }
      }, { merge: true });

      // Peer connection (solo receptor)
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      pcRef.current = pc;

      pc.ontrack = (e) => {
        if (videoRef.current && e.streams[0]) {
          videoRef.current.srcObject = e.streams[0];
        }
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          addDoc(icePhoneRef, { sessionId: sId, candidate: e.candidate.toJSON() }).catch(() => {});
        }
      };
      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === 'connected') {
          setVideoConectando(false);
          setVideoConectado(true);
        }
        if (st === 'failed' || st === 'disconnected' || st === 'closed') {
          if (videoSessionRef.current === sId) {
            setVideoErr('Conexión perdida');
            stopVideo();
          }
        }
      };

      const docRef = doc(db, 'controlRemoto', user.uid);

      // Escuchar offer de tablet → contestar
      const unsubDoc = onSnapshot(docRef, async (snap) => {
        const d = snap.data();
        if (d?.video?.sessionId !== sId) return;
        const offer = d?.video?.offer;
        if (offer && !pc.currentRemoteDescription) {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await updateDoc(docRef, { 'video.answer': { type: answer.type, sdp: answer.sdp } });
          } catch (e) {
            console.error('Error procesando offer:', e);
            setVideoErr('Error de negociación');
          }
        }
      });

      // Escuchar ICE de tablet
      const unsubIce = onSnapshot(iceTabletRef, (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'added') {
            const d = change.doc.data();
            if (d.sessionId !== sId) return;
            if (!pc.currentRemoteDescription) return;
            pc.addIceCandidate(new RTCIceCandidate(d.candidate)).catch(() => {});
          }
        });
      });

      videoUnsubsRef.current = [unsubDoc, unsubIce];

      // Timeout de 15s para conectar
      setTimeout(() => {
        if (videoSessionRef.current === sId && !videoConectado) {
          setVideoErr('La tablet no respondió. ¿Está conectada al ESP32?');
          stopVideo();
        }
      }, 15000);
    } catch (err) {
      console.error('Error iniciando video:', err);
      setVideoErr(err.message || String(err));
      stopVideo();
    }
  }, [user, db, videoConectando, videoConectado, stopVideo]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (videoSessionRef.current) stopVideo();
    };
  }, [stopVideo]);

  // ── UI ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Smartphone size={18} className="text-blue-400" />
          <span className="text-sm font-bold text-slate-300">Control Remoto</span>
        </div>
        <div className="flex items-center gap-2">
          {bateria && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${
              bateria.charging
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                : bateria.level <= 20
                  ? 'bg-red-500/10 text-red-400 border-red-500/30'
                  : 'bg-slate-700/40 text-slate-300 border-slate-600/40'
            }`} title="Batería de la tablet">
              {bateria.charging ? <BatteryCharging size={11} /> : <Battery size={11} />}
              {bateria.level}%
            </div>
          )}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${
            bleConectado
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
              : 'bg-orange-500/10 text-orange-400 border border-orange-500/30'
          }`}>
            {bleConectado ? <Wifi size={11} /> : <WifiOff size={11} />}
            {bleConectado ? 'BLE OK' : 'Sin BLE'}
          </div>
          <button
            onClick={onSignOut}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400"
            title="Cerrar sesión"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>

      {/* Aviso si sin BLE */}
      {!bleConectado && (
        <div className="mb-3 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs">
          ⚠️ La tablet perdió la conexión con el ESP32. Los comandos no surtirán efecto hasta que se reconecte.
        </div>
      )}

      {/* Snapshot + nombre */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-4 mb-3">
        <div className="text-center mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
            {modo === 'clavado' ? 'Clavado Perimetral' : modo === 'tiktok' ? 'TikTok Live' : 'Hilando'}
          </div>
          <div className="text-base font-bold text-white truncate">
            {estado?.secuenciaNombre || (modo === 'clavado' ? 'Clavado' : 'Sin secuencia')}
          </div>
        </div>

        {/* Visualización */}
        <div className="flex justify-center my-3">
          <div className="w-52 h-52 rounded-full overflow-hidden border-4 border-slate-700 bg-slate-900 flex items-center justify-center">
            {svgMarkup ? (
              <div dangerouslySetInnerHTML={{ __html: svgMarkup }} className="w-full h-full" />
            ) : (
              <div className="text-slate-600 text-sm">{modo === 'clavado' ? '🔨' : '—'}</div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-center mt-3">
          <div className="bg-slate-900/60 rounded-xl py-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Progreso</div>
            <div className="text-lg font-bold text-blue-400">{progresoPct}%</div>
          </div>
          <div className="bg-slate-900/60 rounded-xl py-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Posición</div>
            <div className="text-lg font-bold text-white">
              <span className="text-blue-400">{idxActual + 1}</span>
              <span className="text-slate-500 text-xs">/{totalPos}</span>
            </div>
          </div>
          <div className="bg-slate-900/60 rounded-xl py-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Restante</div>
            <div className="text-lg font-bold text-white">{formatTiempo(tiempoRestante)}</div>
          </div>
        </div>

        {/* Hora estimada de fin */}
        {horaEstimada && (
          <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <Clock size={12} />
            <span>Termina ~</span>
            <span className="font-mono font-bold text-blue-300">{horaEstimada}</span>
          </div>
        )}

        {/* Barra progreso */}
        <div className="mt-3 h-2 bg-slate-900 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500" style={{ width: `${progresoPct}%` }} />
        </div>

        {/* Estado actual */}
        <div className="mt-3 flex items-center justify-center gap-2 text-xs">
          <div className={`w-2 h-2 rounded-full ${
            ejecutando
              ? pausado
                ? 'bg-yellow-500'
                : esperandoTimelapse
                  ? 'bg-purple-500 animate-pulse'
                  : 'bg-green-500 animate-pulse'
              : 'bg-slate-500'
          }`}></div>
          <span className="text-slate-300 font-medium">
            {ejecutando
              ? pausado
                ? 'Pausado'
                : esperandoTimelapse
                  ? <span className="flex items-center gap-1"><Camera size={11} /> En posición 0…</span>
                  : 'En curso'
              : 'Detenido'}
          </span>
        </div>
      </div>

      {/* Botón Ver cámara (solo admin) */}
      {isAdmin && (
        <button
          onClick={startVideo}
          disabled={videoConectando || !bleConectado}
          className="w-full mb-3 p-3 bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/40 rounded-xl text-indigo-200 font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
        >
          <Video size={18} />
          {videoConectando ? 'Conectando cámara…' : 'Ver cámara de la tablet'}
        </button>
      )}

      {/* Modal de video */}
      {videoModal && (
        <div className="fixed inset-0 z-[10000] bg-black flex flex-col">
          <div className="flex items-center justify-between p-3 bg-slate-900/80 backdrop-blur-sm border-b border-slate-700">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${videoConectado ? 'bg-red-500 animate-pulse' : 'bg-yellow-500 animate-pulse'}`}></div>
              <span className="text-white text-xs font-bold tracking-wider">
                {videoConectado ? 'EN VIVO' : 'Conectando…'}
              </span>
            </div>
            <button
              onClick={stopVideo}
              className="p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white"
              title="Cerrar"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center bg-black overflow-hidden">
            {!videoConectado && (
              <div className="absolute flex flex-col items-center gap-3 text-slate-400">
                <Loader size={36} className="animate-spin" />
                <span className="text-sm">Esperando a la tablet…</span>
                <span className="text-[10px] text-slate-600">Acepta el permiso de cámara en la tablet</span>
              </div>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {videoErr && (
            <div className="p-3 bg-red-900/80 text-red-100 text-xs text-center">
              ⚠️ {videoErr}
            </div>
          )}
        </div>
      )}

      {/* Alertas activas */}
      {alertas.hiloRoto && (
        <button
          onClick={() => enviarComando('reparado')}
          disabled={enviando}
          className="w-full mb-3 p-4 bg-red-900/40 border-2 border-red-500/60 rounded-2xl text-left animate-pulse disabled:animate-none disabled:opacity-70"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-400 shrink-0" size={28} />
            <div className="flex-1">
              <div className="text-red-200 font-bold">⚠️ Hilo roto</div>
              <div className="text-red-300/80 text-xs mt-0.5">Toca para marcar como reparado</div>
            </div>
            {enviando && comandoPendiente?.accion === 'reparado' && (
              <Loader size={18} className="animate-spin text-red-300" />
            )}
          </div>
        </button>
      )}

      {alertas.clavosAgotados && (
        <button
          onClick={() => enviarComando('recargarClavos')}
          disabled={enviando}
          className="w-full mb-3 p-4 bg-orange-900/40 border-2 border-orange-500/60 rounded-2xl text-left disabled:opacity-70"
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl">🔨</div>
            <div className="flex-1">
              <div className="text-orange-200 font-bold">Clavos agotados</div>
              <div className="text-orange-300/80 text-xs mt-0.5">Toca para recargar (90)</div>
            </div>
            {enviando && comandoPendiente?.accion === 'recargarClavos' && (
              <Loader size={18} className="animate-spin text-orange-300" />
            )}
          </div>
        </button>
      )}

      {/* Botón principal: Play / Pause */}
      <button
        onClick={() => enviarComando(ejecutando && !pausado ? 'pausar' : 'reanudar')}
        disabled={enviando || (!estado?.secuenciaId && modo !== 'clavado')}
        className={`w-full h-24 rounded-2xl font-bold text-white text-xl shadow-xl transition-all flex flex-col items-center justify-center gap-1 ${
          ejecutando && !pausado
            ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 active:from-yellow-600 active:to-yellow-700'
            : 'bg-gradient-to-br from-emerald-500 to-emerald-600 active:from-emerald-600 active:to-emerald-700'
        } disabled:from-slate-600 disabled:to-slate-700 disabled:opacity-60 active:scale-95`}
      >
        {enviando ? (
          <Loader size={32} className="animate-spin" />
        ) : ejecutando && !pausado ? (
          <>
            <Pause size={36} />
            <span>Pausar</span>
          </>
        ) : (
          <>
            <Play size={36} className="ml-1" />
            <span>{pausado ? 'Reanudar' : 'Iniciar'}</span>
          </>
        )}
      </button>

      {error && (
        <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs text-center">
          {error}
        </div>
      )}

      {/* Abrir app completa en este dispositivo (útil en la computadora) */}
      {onUsarAppCompleta && (
        <button
          onClick={onUsarAppCompleta}
          className="w-full mt-3 p-2.5 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 text-slate-300 text-xs font-bold flex items-center justify-center gap-2 transition-colors"
        >
          <ExternalLink size={14} />
          Abrir app completa aquí
        </button>
      )}

      {/* Footer */}
      <div className="mt-4 text-center text-[10px] text-slate-600">
        Sesión: <span className="font-mono">{sessionId?.slice(0, 8)}</span> · {user.email}
      </div>
    </div>
  );
};

export default RemoteControl;
